import React, { useState } from 'react';
import {
  Card,
  Select,
  Spinner,
  TextField,
} from 'ui-component-library';
import { func } from 'prop-types';
import * as yup from 'yup';

import InfoSection from '../../common/card-shared-components/card-info-section/card-info-section';
import LabelValue from '../../common/card-shared-components/card-label-value/card-label-value';
import {
  HeaderButtonsGroup,
  HeaderButton,
  LoadingContainer,
  EquipmentEditMessage,
} from './card-driver-truck-info.style';
import InfoTooltipButton from '../../common/info-tooltip-button/info-tooltip-button';
import { TOOLTIP_MESSAGES } from '../../../../constants';
import editIcon from '../../../../assets/icon-pencil.svg';
import lockShape from '../../../../shapes/lock';
import loadShape from '../../../../shapes/load';
import { updateDriverInformation, updateDispatcherInformation, updateEquipmentInformation } from '../../../../utils/api/load';
import { showErrorMessage, showSuccessMessage } from '../../../../utils/common';
import { formatPhoneNumber } from '../../../../utils/formatters';
import ImageIcon from '../../common/image-icon';

// Equipment info validation schema
export const equipmentInfoSchema = yup.object().shape({
  truckNumber: yup.string().max(50, 'Truck Number cannot be more than 50 characters'),
  trailerNumber: yup.string().max(9, 'Trailer Number cannot be more than 9 characters'),
});

/**
 * Driver truck info component
 * @param {object} params - Object containing props
 * @param {object} params.load - Load object
 * @param {func} params.refetchLoad - Function to refetch the load
 * @return {element} - Driver Truck Info component
 */
const DriverTruckInfo = ({
  lock,
  load,
  refetchLoad,
}) => {
  const data = load.driverInformation;

  // Input state hooks
  const [truckNumber, setTruckNumber] = useState(data?.truckNumber);
  const [trailerNumber, setTrailerNumber] = useState(data?.trailerNumber);
  const [driverInfo, setDriverInfo] = useState({
    label: data?.driverName,
    value: {
      driverId: data?.driverId,
      driverName: data?.driverName,
      driverPhoneNumber: data?.driverPhoneNumber,
    },
  });
  const [dispatcherInfo, setDispatcherInfo] = useState({
    label: data?.dispatcherName,
    value: {
      dispatcherId: data?.dispatcherId,
      dispatcherName: data?.dispatcherName,
      dispatcherPhoneNumber: data?.dispatcherPhoneNumber,
      dispatcherEmail: data?.dispatcherEmail,
    },
  });

  // Component state hooks
  const [driverInfoLoading, setDriverInfoLoading] = useState(false); // If async loading driver info
  const [equipmentInfoLoading, setEquipmentInfoLoading] = useState(false); // If async loading equipment info
  const [dispatcherInfoLoading, setDispatcherInfoLoading] = useState(false); // If async loading dipatcher info
  const [editingDispatcherInfo, setEditingDispatcherInfo] = useState(false); // If editing dispatcher info
  const [editingEquipment, setEditingEquipment] = useState(false); // If editing equipment info
  const [editingDriverInfo, setEditingDriverInfo] = useState(false); // If editing driver info

  /**
   * Constructs driver options to use with a select
   * @param {object[]} options - List of options to convert to input options
   * @returns {object[]} - List of options that can be used with a select
   */
  const constructDriverOptions = (options) => (
    options.map((option) => (
      {
        label: option.driverName,
        value: option,
      }
    ))
  );

  /**
   * Constructs dispatcher options to use with a select
   * @param {object[]} options - List of options to convert to input options
   * @returns {object[]} - List of options that can be used with a select
   */
  const constructDispatcherOptions = (options) => (
    options.map((option) => (
      {
        label: option.dispatcherName,
        value: option,
      }
    ))
  );

  /**
   * Handles when the truck number changes
   * @param {object} event - DOM Event
   */
  const onTruckNumberChanged = (event) => {
    if (equipmentInfoSchema.fields.truckNumber.isValidSync(event.target.value)) {
      setTruckNumber(event.target.value);
    }
  };

  /**
   * Handles when the trailer number changes
   * @param {object} event - DOM Event
   */
  const onTrailerNumberChanged = (event) => {
    if (equipmentInfoSchema.fields.trailerNumber.isValidSync(event.target.value)) {
      setTrailerNumber(event.target.value);
    }
  };

  /**
   * Handles when the driver info changes
   * @return {void}
   **/
  const onDriverInfoChanged = (event) => {
    setDriverInfo(event);
  };

  /**
   * Handles when the dispatcher info changes
   * @return {void}
   **/
  const onDispatcherChanged = (event) => {
    setDispatcherInfo(event);
  };

  /**
   * Handles when equipement number has been
   * updated.
   * @return {void}
   */
  const onUpdateEquipment = () => {
    if (equipmentInfoSchema.isValidSync({ truckNumber, trailerNumber })) {
      const payload = {
        loadId: load.loadId,
        salesOrderBk: load.billOfLadingNumber,
        truckNumber,
        trailerNumber,
      };
      setEquipmentInfoLoading(true);
      updateEquipmentInformation(payload).then(() => {
        showSuccessMessage(`Equipment information has been updated for load #${load.billOfLadingNumber}. It may take a moment to update.`);
        refetchLoad({
          driverInformation: {
            ...load.driverInformation,
            truckNumber,
            trailerNumber,
          },
        });
      }).catch(() => {
        showErrorMessage(`Could not update equipment information for load #${load.billOfLadingNumber}`);
      }).finally(() => {
        setEquipmentInfoLoading(false);
      });
      setEditingEquipment(false);
    }
  };

  /**
   * Handles when a user updates the driver information.
   * @return {void}
   */
  const onUpdateDriverInfo = () => {
    const payload = {
      loadId: load.loadId,
      salesOrderBk: load.billOfLadingNumber,
      driverId: driverInfo.value.driverId,
    };
    setDriverInfoLoading(true);
    updateDriverInformation(payload).then(() => {
      showSuccessMessage(`Driver information has been updated for load #${load.billOfLadingNumber}. It may take a moment to update.`);
      refetchLoad({
        driverInformation: {
          ...load.driverInformation,
          driverName: driverInfo.value.driverName,
          driverPhoneNumber: driverInfo.value.driverPhoneNumber,
        },
      });
    }).catch(() => {
      showErrorMessage(`Could not update driver information for load #${load.billOfLadingNumber}`);
    }).finally(() => {
      setDriverInfoLoading(false);
    });
    setEditingDriverInfo(false);
  };

  /**
   * Handles when a user updates the dispatcher information.
   * @return {void}
   */
  const onUpdateDispatcherInfo = () => {
    const payload = {
      loadId: load.loadId,
      salesOrderBk: load.billOfLadingNumber,
      dispatcherId: dispatcherInfo.value.dispatcherId,
    };
    setDispatcherInfoLoading(true);
    updateDispatcherInformation(payload).then(() => {
      showSuccessMessage(`Dispatcher information has been updated for load #${load.billOfLadingNumber}. It may take a moment to update.`);
      refetchLoad({
        dispatcherInfo: {
          ...load.driverInformation,
          dispatcherName: dispatcherInfo.value.dispatcherName,
          dispatcherPhoneNumber: dispatcherInfo.value.dispatcherPhoneNumber,
          dispatcherEmail: dispatcherInfo.value.dispatcherEmail,
        },
      });
    }).catch(() => {
      showErrorMessage(`Could not update dispatcher information for load #${load.billOfLadingNumber}`);
    }).finally(() => {
      setDispatcherInfoLoading(false);
    });
    setEditingDispatcherInfo(false);
  };

  /**
   * Helper function to render the header content
   * for the equipment information info section
   * @return {element}
   */
  const renderEquipmentHeaderContent = () => {
    if (lock?.loadId && !lock?.ownedByUser) {
      return null;
    }
    return (!editingEquipment ? (
      <ImageIcon
        height={15}
        width={15}
        src={editIcon}
        onClick={() => setEditingEquipment(true)}
        altString="Edit Icon"
        offCenter
      />
    ) : (
      <HeaderButtonsGroup>
        <HeaderButton onClick={() => setEditingEquipment(false)} type="cancel" text="Cancel" />
        <HeaderButton onClick={onUpdateEquipment} type="submit" text="Save" />
      </HeaderButtonsGroup>
    ));
  };

  /**
   * Helper function to render the header content
   * for the driver information section
   * @return {element}
   */
  const renderDriverInfoHeaderContent = () => {
    if (lock?.loadId && !lock?.ownedByUser) {
      return null;
    }
    return (!editingDriverInfo ? (
      <ImageIcon
        height={15}
        width={15}
        src={editIcon}
        onClick={() => setEditingDriverInfo(true)}
        altString="Edit Icon"
        offCenter
      />
    ) : (
      <HeaderButtonsGroup>
        <HeaderButton onClick={() => setEditingDriverInfo(false)} type="cancel" text="Cancel" />
        <HeaderButton onClick={onUpdateDriverInfo} type="submit" text="Save" />
      </HeaderButtonsGroup>
    ));
  };

  /**
   * Renders dispatcher information header content
   * @return {element}
   */
  const renderDispatcherInfoHeaderContent = () => {
    if (lock?.loadId && !lock?.ownedByUser) {
      return null;
    }
    return (!editingDispatcherInfo ? (
      <ImageIcon
        height={15}
        width={15}
        src={editIcon}
        onClick={() => setEditingDispatcherInfo(true)}
        altString="Edit Icon"
        offCenter
      />
    ) : (
      <HeaderButtonsGroup>
        <HeaderButton onClick={() => setEditingDispatcherInfo(false)} type="cancel" text="Cancel" />
        <HeaderButton onClick={onUpdateDispatcherInfo} type="submit" text="Save" />
      </HeaderButtonsGroup>
    ));
  };

  /**
   * Edit trailer number control
   * @return {element}
   */
  const editTrailerNumberField = (
    <TextField
      name="label"
      value={trailerNumber}
      onValueChange={onTrailerNumberChanged}
    />
  );

  /**
   * Edit truck number control
   * @return {element}
   */
  const editTruckNumberField = (
    <TextField
      value={truckNumber}
      onValueChange={onTruckNumberChanged}
    />
  );

  /**
   * Control field to edit driver name
   * @return {element}
   */
  const editDriverNameField = (
    <Select
      options={constructDriverOptions(load.carrierDrivers || [])}
      selectedValue={driverInfo}
      onValueChange={onDriverInfoChanged}
    />
  );

  /**
   * Control field to edit dispatcher name
   * @return {element}
   */
  const editDispatcherNameField = (
    <Select
      options={constructDispatcherOptions(load.carrierDispatchers || [])}
      selectedValue={dispatcherInfo}
      onValueChange={onDispatcherChanged}
    />
  );

  if (!data) {
    return null;
  }

  return (
    <Card
      title="Dispatcher, Driver & Equipment"
      headerContent={<InfoTooltipButton text={TOOLTIP_MESSAGES['driver-truck-info']} isPrimary />}
      isPaddingRemoved
    >
      <InfoSection numCols={3} header="Dispatcher Information" isPaddingRemoved headerContent={renderDispatcherInfoHeaderContent()}>
        {!dispatcherInfoLoading && (
          <>
            <LabelValue
              label="Dispatcher Name"
              value={data.dispatcherName}
              options={{ stacked: true }}
              editOptions={{
                editing: editingDispatcherInfo,
                editNode: editDispatcherNameField,
              }}
            />
            <LabelValue
              label="Dispatcher Mobile"
              value={formatPhoneNumber(editingDispatcherInfo ? dispatcherInfo.value.dispatcherPhoneNumber
                : data.dispatcherPhoneNumber)}
              options={{ stacked: true }}
            />
            <LabelValue
              label="Dispatcher Email"
              value={editingDispatcherInfo ? dispatcherInfo.value.dispatcherEmail
                : data.dispatcherEmail}
              options={{ stacked: true }}
            />
          </>
        )}
        {dispatcherInfoLoading && <LoadingContainer><Spinner /></LoadingContainer>}
      </InfoSection>
      <InfoSection numCols={3} header="Driver Information" isPaddingRemoved headerContent={renderDriverInfoHeaderContent()}>
        {!driverInfoLoading && (
          <>
            <LabelValue
              label="Driver Name"
              value={data.driverName}
              options={{ stacked: true }}
              editOptions={{
                editing: editingDriverInfo,
                editNode: editDriverNameField,
              }}
            />
            <LabelValue
              label="Driver Mobile"
              value={formatPhoneNumber(editingDriverInfo ? driverInfo.value.driverPhoneNumber : data.driverPhoneNumber)}
              options={{ stacked: true }}
            />
          </>
        )}
        {driverInfoLoading && <LoadingContainer><Spinner /></LoadingContainer>}
      </InfoSection>
      <InfoSection header="Equipment Information" headerContent={renderEquipmentHeaderContent()}>
        {!equipmentInfoLoading && (
          <>
            {editingEquipment && (
              <EquipmentEditMessage>
                Equipment type, length and weight fields can only be edited in TMS.
              </EquipmentEditMessage>
            )}
            <InfoSection numCols={3} rowGap={8} removePadding>
              <LabelValue label="Equipment" value={data.equipment} options={{ stacked: true }} />
              <LabelValue label="Length" value={`${data.length} ft`} options={{ stacked: true }} />
              <LabelValue label="Weight" value={`${data.weight} ${data.weightUnit}`} options={{ stacked: true }} />
              <LabelValue
                label="Truck Number"
                value={data.truckNumber}
                options={{ stacked: true }}
                editOptions={{
                  editing: editingEquipment,
                  editNode: editTruckNumberField,
                }}
              />
              <LabelValue
                label="Trailer Number"
                value={data.trailerNumber}
                options={{ stacked: true }}
                editOptions={{
                  editing: editingEquipment,
                  editNode: editTrailerNumberField,
                }}
              />
            </InfoSection>
          </>
        )}
        {equipmentInfoLoading && <LoadingContainer><Spinner /></LoadingContainer>}
      </InfoSection>
    </Card>
  );
};

DriverTruckInfo.propTypes = {
  load: loadShape,
  lock: lockShape,
  refetchLoad: func,
};

DriverTruckInfo.defaultProps = {
  load: null,
  lock: null,
  refetchLoad: () => { },
};

export default DriverTruckInfo;
